import { useState } from 'react';
import type { TenantInvoice } from '@yawi/types';
import { formatCurrency } from '@yawi/infrastructure';
import { Badge, Button, Card } from '@/core/ui/components';
import { Receipt, Plus, Check, X } from 'lucide-react';
import { useConfirmation } from '@/app/providers';

const STATUS_VARIANTS: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  DRAFT: 'default',
  PENDING: 'warning',
  PAID: 'success',
  OVERDUE: 'error',
  CANCELLED: 'default',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Borrador',
  PENDING: 'Pendiente',
  PAID: 'Pagada',
  OVERDUE: 'Vencida',
  CANCELLED: 'Cancelada',
};

interface TenantInvoicesTableProps {
  invoices: TenantInvoice[];
  onGenerate: () => void;
  onMarkPaid: (invoice: TenantInvoice) => void;
  onCancel: (invoiceId: string) => void;
}

export function TenantInvoicesTable({ invoices, onGenerate, onMarkPaid, onCancel }: TenantInvoicesTableProps) {
  const { confirm } = useConfirmation();

  const formatDate = (d: string) => new Date(d).toLocaleDateString('es-MX', {
    year: 'numeric', month: 'short', day: 'numeric',
  });

  const handleCancel = async (invoice: TenantInvoice) => {
    const confirmed = await confirm({
      title: 'Cancelar Factura',
      message: `¿Cancelar la factura ${invoice.invoiceNumber}?`,
      variant: 'danger',
      confirmText: 'Cancelar factura',
    });
    if (confirmed) {
      onCancel(invoice.id);
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Receipt className="w-5 h-5 text-theme-secondary" />
          <h3 className="text-lg font-semibold text-theme">Facturas</h3>
        </div>
        <Button
          size="sm"
          leftIcon={<Plus className="w-4 h-4" />}
          onClick={onGenerate}
        >
          Generar
        </Button>
      </div>

      {invoices.length === 0 ? (
        <p className="text-theme-secondary text-center py-6">No hay facturas</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-theme-tertiary border-b border-theme">
                <th className="pb-3 font-medium">Factura</th>
                <th className="pb-3 font-medium">Periodo</th>
                <th className="pb-3 font-medium">Estado</th>
                <th className="pb-3 font-medium text-right">Total</th>
                <th className="pb-3 font-medium">Vencimiento</th>
                <th className="pb-3 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {invoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-3">
                    <span className="font-medium text-theme">{invoice.invoiceNumber}</span>
                  </td>
                  <td className="py-3 text-theme-secondary">{invoice.period}</td>
                  <td className="py-3">
                    <Badge variant={STATUS_VARIANTS[invoice.status] || 'default'}>
                      {STATUS_LABELS[invoice.status] || invoice.status}
                    </Badge>
                  </td>
                  <td className="py-3 text-right font-medium text-theme">
                    {formatCurrency(invoice.totalAmount, invoice.currency)}
                  </td>
                  <td className="py-3 text-theme-secondary">{formatDate(invoice.dueDate)}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {(invoice.status === 'PENDING' || invoice.status === 'OVERDUE') && (
                        <Button
                          variant="outline"
                          size="sm"
                          leftIcon={<Check className="w-3 h-3" />}
                          onClick={() => onMarkPaid(invoice)}
                        >
                          Pagada
                        </Button>
                      )}
                      {invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600"
                          leftIcon={<X className="w-3 h-3" />}
                          onClick={() => handleCancel(invoice)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
